import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { bodyTooltip, chartTitle, type OptionContext } from './shared.ts';

type DivergingConfig = Extract<ChartConfig, { type: 'diverging_bar' }>;

function stanceFill(stance: 'praise' | 'friction', dark: boolean): string {
  if (stance === 'friction') return dark ? '#8b4554' : '#7a3d4a';
  return dark ? '#3d8f8a' : '#347a75';
}

/**
 * Diverging horizontal bars — friction left / praise right per row (list shares).
 */
export function buildDivergingBarOption(
  config: DivergingConfig,
  { theme, base }: OptionContext
): EChartsOption {
  const { textColor, dark, axisLineColor } = theme;
  const compact = config.compact === true;
  const items = config.items;
  const labels = items.map((i) => i.name);
  const max = Math.max(
    1,
    ...items.flatMap((i) => [Math.abs(i.praise), Math.abs(i.friction)])
  );
  const axisMax = Math.ceil(max * 1.15);

  const frictionData = items.map((item) => {
    const v = Math.abs(item.friction);
    return {
      value: v > 0 ? -v : 0,
      itemStyle: {
        color: stanceFill('friction', dark),
        borderRadius: compact ? [2, 0, 0, 2] : [4, 0, 0, 4],
      },
    };
  });

  const praiseData = items.map((item) => {
    const v = Math.abs(item.praise);
    return {
      value: v,
      itemStyle: {
        color: stanceFill('praise', dark),
        borderRadius: compact ? [0, 2, 2, 0] : [0, 4, 4, 0],
      },
    };
  });

  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params];
        const first = rows[0] as { name?: string; dataIndex?: number };
        const name = first.name ?? 'Emotion';
        const idx = first.dataIndex ?? 0;
        const item = items[idx];
        if (!item) return name;
        const lines = [name];
        if (item.praise > 0) lines.push(`Praise: ${item.praise}%`);
        if (item.friction > 0) lines.push(`Friction: ${item.friction}%`);
        return lines.join('<br/>');
      },
    }),
    grid: {
      left: compact ? 0 : 8,
      right: compact ? 2 : 12,
      top: config.title ? 28 : compact ? 0 : 8,
      bottom: compact ? 0 : 8,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      min: -axisMax,
      max: axisMax,
      axisLabel: compact
        ? { show: false }
        : {
            color: textColor,
            fontSize: 10,
            formatter: (v: number) => `${Math.abs(v)}%`,
          },
      axisLine: compact
        ? { show: false }
        : { lineStyle: { color: axisLineColor } },
      splitLine: compact
        ? { show: false }
        : {
            show: true,
            lineStyle: { color: axisLineColor, type: 'dashed' as const },
          },
    },
    yAxis: {
      type: 'category',
      data: labels,
      inverse: true,
      axisLabel: {
        color: textColor,
        fontSize: compact ? 12 : 11,
        width: compact ? 88 : 110,
        margin: compact ? 4 : 8,
        overflow: 'truncate' as const,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Friction',
        type: 'bar' as const,
        stack: 'stance',
        data: frictionData,
        barMaxWidth: compact ? 11 : 22,
        barGap: '-100%',
        barCategoryGap: compact ? '12%' : '20%',
        animation: false,
        markLine: compact
          ? undefined
          : {
              silent: true,
              symbol: 'none' as const,
              label: { show: false },
              lineStyle: {
                color: axisLineColor,
                type: 'solid' as const,
                width: 1,
              },
              data: [{ xAxis: 0 }],
            },
        emphasis: {
          itemStyle: {
            borderColor: axisLineColor,
            borderWidth: 1,
          },
        },
      },
      {
        name: 'Praise',
        type: 'bar' as const,
        stack: 'stance',
        data: praiseData,
        barMaxWidth: compact ? 11 : 22,
        barCategoryGap: compact ? '12%' : '20%',
        animation: false,
        emphasis: {
          itemStyle: {
            borderColor: axisLineColor,
            borderWidth: 1,
          },
        },
      },
    ],
  };
}
